import { Button, OverlaySpinner } from 'components/common'
import React, { useRef, useState } from 'react'
import AutoCompleteInput from 'components/GoogleAutoComplete/AutoCompleteInput'
import GoogleAutoComplete from 'components/GoogleAutoComplete/GoogleAutoComplete'
import { geoLocationErrorHandler, getPosition } from 'utils'
import { getAddressByCoordinate } from 'actions'
import CustomDeliveryModal from 'components/OrderSteps/common/CustomDeliveryModal'
import { ModalRef } from 'components/common/Modal/Modal'
import Box from '../common/Box'
import { AddressDetails, DeliveryPlace, Island } from 'components/common/types'
import OrderStepsTemplate from '../OrderStepsTemplate'
import {
  useAppDispatch,
  useAppSelector
} from 'components/hooks/redux-selectors'
import CustomAddressSelect, {
  ParishOption
} from '../common/CustomAddressSelect'

type Props = {
  submitButtonText: string
  setSelectedAddressDetails: (params: Partial<AddressDetails>) => void
  title: string
  selectedAddressDetails: AddressDetails
  showLeftArrow: boolean
  showCoordinateBox: boolean
  goPrevious?: () => void
  coordinateBoxTextNode: string | React.ReactChild
  customSomeWhereElseTextNode: string | React.ReactChild
  onSubmit: () => void
  isoCode: string
}

const DeliveryAreaStep = ({
  submitButtonText,
  title,
  selectedAddressDetails,
  showLeftArrow = true,
  showCoordinateBox = true,
  goPrevious,
  setSelectedAddressDetails,
  customSomeWhereElseTextNode,
  coordinateBoxTextNode,
  onSubmit,
  isoCode
}: Props) => {
  const {
    locations: { data: locations, isLoading: locationsLoading }
  } = useAppSelector((state) => state.order)
  const [buttonLoading, setButtonLoading] = useState(false)
  const isIsoCodeExist = isoCode !== null

  const addressModalRef = useRef<ModalRef>(null)

  const dispatch = useAppDispatch()

  const [
    {
      island,
      parishTown,
      town,
      filteredParishes,
      parish,
      streetDetails,
      googleResponse,
      address,
      deliveryPlace,
      latitude,
      longitude
    },
    setAddressDetails
  ] = useState<AddressDetails>(selectedAddressDetails)

  const handleAddressDetailsChange = (value) => {
    setAddressDetails((prev) => ({
      ...prev,
      ...value
    }))
  }

  const handleSelectParish = (option: ParishOption) => {
    handleAddressDetailsChange({
      town: option.town,
      parishTown: option.label,
      parish: option.parish
    })
  }

  const handleLocationSelect = (island: Island) => {
    const foundParishes = locations?.cities?.find(
      (item) => item.name === island.label
    )?.parishes

    handleAddressDetailsChange({
      filteredParishes: foundParishes,
      island: { label: island.label, value: island.value }
    })
  }

  const handleCoordinateClick = async () => {
    try {
      setButtonLoading(true)

      const { position } = await getPosition().catch((error) => {
        geoLocationErrorHandler(error)

        throw error
      })

      const latitude = position.coords.latitude
      const longitude = position.coords.longitude

      await dispatch(getAddressByCoordinate(latitude, longitude)).then(
        (response) =>
          handleAddressDetailsChange({
            address: response?.FullAddress,
            latitude,
            longitude,
            deliveryPlace: DeliveryPlace.COORDINATE
          })
      )
    } catch (error) {
      handleAddressDetailsChange({
        deliveryPlace: ''
      })
    }

    setButtonLoading(false)
  }

  const disableConfirmButton = () => {
    return isIsoCodeExist
      ? typeof googleResponse?.name === 'undefined'
      : !parish || !town || !island || streetDetails.trim() === ''
  }

  const handleCustomClick = () => {
    handleAddressDetailsChange({
      deliveryPlace: DeliveryPlace.CUSTOM,
      address: ''
    })
    addressModalRef.current!.openModal()
  }

  const customConfirmHandler = () => addressModalRef.current!.closeModal()

  const customCancelHandler = () => {
    if (isIsoCodeExist) {
      handleAddressDetailsChange({
        deliveryPlace: '',
        googleResponse: undefined
      })
    } else {
      handleAddressDetailsChange({
        deliveryPlace: '',
        parishTown: '',
        streetDetails: '',
        town: '',
        parish: '',
        island: ''
      })
    }

    addressModalRef.current!.closeModal()
  }

  const isCoordinateDelivery = deliveryPlace === DeliveryPlace.COORDINATE
  const isCustomDelivery =
    deliveryPlace === DeliveryPlace.CUSTOM ||
    deliveryPlace === DeliveryPlace.PROFILE_ADDRESS

  const handleDeliverDetailsSet = () => {
    const isGoogleSearch = isIsoCodeExist

    let addressDetails

    if (isGoogleSearch && DeliveryPlace.CUSTOM) {
      addressDetails = googleResponse?.formattedAddress
    }

    if (!isGoogleSearch && DeliveryPlace.CUSTOM) {
      addressDetails = `${streetDetails}, ${town}, ${parish}, ${island.label}`
    }

    if (
      deliveryPlace === DeliveryPlace.COORDINATE ||
      deliveryPlace === DeliveryPlace.PROFILE_ADDRESS
    ) {
      addressDetails = address
    }

    setSelectedAddressDetails({
      island,
      parishTown,
      town,
      filteredParishes,
      parish,
      streetDetails:
        deliveryPlace === DeliveryPlace.CUSTOM ? streetDetails : '',
      googleResponse,
      address: addressDetails,
      deliveryPlace,
      latitude,
      longitude
    })
  }

  const handleSubmit = () => {
    handleDeliverDetailsSet()

    onSubmit()
  }

  const handlePrevArrowClick = () => {
    handleDeliverDetailsSet()

    if (showLeftArrow) {
      goPrevious()
    }
  }

  return (
    <>
      {(locationsLoading || buttonLoading) && <OverlaySpinner />}

      <OrderStepsTemplate
        showLeftArrow={showLeftArrow}
        title={title}
        prevArrowClick={handlePrevArrowClick}
        buttonNode={
          <Button
            onClick={handleSubmit}
            variant="active"
            isLoading={buttonLoading}
            text={submitButtonText}
            disabled={deliveryPlace === ''}
          />
        }
      >
        <div className="flex flex-1 flex-col space-y-6">
          {showCoordinateBox && (
            <Box
              isActive={isCoordinateDelivery}
              onClick={handleCoordinateClick}
              className="basis-1/2 items-center justify-center space-y-4"
            >
              {coordinateBoxTextNode}

              <div className="w-[90%] pr-3 text-center sm:w-full"></div>
              {deliveryPlace === DeliveryPlace.COORDINATE && (
                <div className="mr-12 text-center">{address}</div>
              )}
            </Box>
          )}

          <Box
            isActive={isCustomDelivery}
            onClick={handleCustomClick}
            className="basis-1/2 items-center justify-center space-y-4"
          >
            {customSomeWhereElseTextNode}

            <div className="w-[90%] pr-3 text-center sm:w-full">
              {isIsoCodeExist &&
                googleResponse &&
                googleResponse.formattedAddress}

              {parishTown &&
                isCustomDelivery &&
                `${streetDetails}, ${town}, ${parish}, ${island.label}`}

              {deliveryPlace === DeliveryPlace.PROFILE_ADDRESS && (
                <div>{address}</div>
              )}
            </div>
          </Box>
        </div>
      </OrderStepsTemplate>

      <CustomDeliveryModal
        ref={addressModalRef}
        disableConfirmButton={disableConfirmButton()}
        onConfirm={customConfirmHandler}
        onCancel={customCancelHandler}
        title={`Select ${title}`}
      >
        {isIsoCodeExist ? (
          <GoogleAutoComplete>
            <AutoCompleteInput
              isoCode={isoCode}
              googleResponse={googleResponse}
              setGoogleResponse={(response) => {
                handleAddressDetailsChange({ googleResponse: response })
              }}
            />
          </GoogleAutoComplete>
        ) : (
          <CustomAddressSelect
            handleSelectParish={handleSelectParish}
            handleLocationSelect={handleLocationSelect}
            locations={locations}
            parishes={filteredParishes}
            parishTown={parishTown}
            island={island}
          >
            {parishTown && (
              <>
                <div className="mb-4">
                  {streetDetails}, {town}, {parish}, {island.label}
                </div>

                <input
                  className="mt-4 bg-gray-200"
                  placeholder="Street address details *"
                  value={streetDetails}
                  onChange={(e) =>
                    handleAddressDetailsChange({
                      streetDetails: e.target.value
                    })
                  }
                />
              </>
            )}
          </CustomAddressSelect>
        )}
      </CustomDeliveryModal>
    </>
  )
}

export default DeliveryAreaStep
